/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: TagInput.tsx
 🗓️ Created: 2025-12-11
 🧩 Description:
    TagInput component for inputting multiple values as tags.
    Perfect for MAC address prefixes (OUI), keywords, or any
    array of string values.

 🔧 Notes:
    - Supports adding tags on Enter or comma
    - Visual tag display with remove buttons
    - Validation support for specific patterns (e.g., MAC OUI)
    - Modern styling with animations
    - Optional placeholder and disabled states

 🔁 Last Updated: 2025-12-11 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useState, KeyboardEvent } from 'react';
import { X, Plus } from 'lucide-react';
import { Input } from './input';
import { Badge } from './badge';

interface TagInputProps {
  /** Current array of tags */
  value: string[];
  /** Callback when tags change */
  onChange: (tags: string[]) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Custom className for container */
  className?: string;
  /** Validation function for new tags */
  validate?: (tag: string) => boolean;
  /** Error message to show for invalid input */
  errorMessage?: string;
  /** Transform function applied before adding tag (e.g., toUpperCase for MAC) */
  transform?: (tag: string) => string;
  /** Maximum number of tags allowed */
  maxTags?: number;
}

export const TagInput: React.FC<TagInputProps> = ({
  value = [],
  onChange,
  placeholder = 'Type and press Enter...',
  disabled = false,
  className = '',
  validate,
  errorMessage = 'Invalid format',
  transform,
  maxTags
}) => {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const addTag = (rawTag: string) => {
    const tag = transform ? transform(rawTag.trim()) : rawTag.trim();

    if (!tag) return;

    // Check if already exists
    if (value.includes(tag)) {
      setError('Already added');
      setTimeout(() => setError(null), 2000);
      return;
    }

    // Check max tags
    if (maxTags && value.length >= maxTags) {
      setError(`Maximum ${maxTags} items allowed`);
      setTimeout(() => setError(null), 2000);
      return;
    }

    // Validate if function provided
    if (validate && !validate(tag)) {
      setError(errorMessage);
      setTimeout(() => setError(null), 2000);
      return;
    }

    onChange([...value, tag]);
    setInputValue('');
    setError(null);
  };

  const removeTag = (tagToRemove: string) => {
    if (disabled) return;
    onChange(value.filter(tag => tag !== tagToRemove));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      // Remove last tag on backspace if input is empty
      removeTag(value[value.length - 1]);
    }
  };

  const handleBlur = () => {
    // Add tag on blur if there's content
    if (inputValue.trim()) {
      addTag(inputValue);
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Tags Display */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 p-2 bg-muted/30 rounded-lg border border-border/50">
          {value.map((tag, index) => (
            <Badge
              key={`${tag}-${index}`}
              variant="secondary"
              className="flex items-center gap-1 py-1 px-2 bg-background border border-border hover:border-primary/50 transition-all group"
            >
              <span className="font-mono text-xs">{tag}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    removeTag(tag);
                  }}
                  className="p-0.5 rounded-full hover:bg-destructive/20 hover:text-destructive transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}

      {/* Input Field */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled}
            className={`font-mono ${error ? 'border-destructive focus:ring-destructive' : ''}`}
          />
          {error && (
            <span className="absolute -bottom-5 left-0 text-xs text-destructive">
              {error}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => addTag(inputValue)}
          disabled={disabled || !inputValue.trim()}
          className="flex items-center justify-center h-10 w-10 rounded-md border border-border bg-background hover:bg-muted hover:border-primary/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>


    </div>
  );
};

export default TagInput;
